import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock3, Trash2 } from "lucide-react";
import { api, btnGhost, fmtErr, input } from "./shared";

const today = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
const fmtMin = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m` : `${m}m`);
const sel = `${input} !bg-[#0b1220]`;
const dark = { backgroundColor: "#0b1220", colorScheme: "dark" };

export const Permissions = ({ staff = [], canEdit }) => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ email: "", date: today(), hours: "0", minutes: "0", reason: "" });
  const [busy, setBusy] = useState(false);
  const month = f.date.slice(0, 7);
  const load = useCallback(() => api.get(`/biometric/short-permissions?month=${month}`).then((r) => setItems(r.data.items || [])).catch(() => {}), [month]);
  useEffect(() => { load(); }, [load]);
  const mins = Number(f.hours) * 60 + Number(f.minutes);
  const effect = mins > 120 ? "Absent (LOP)" : mins > 60 ? "Half Day" : mins > 0 ? "No impact — Full Day" : "";
  const grant = async () => {
    setBusy(true);
    try { await api.post("/biometric/short-permissions", { ...f, hours: Number(f.hours), minutes: Number(f.minutes) }); toast.success(`Permission saved — ${effect}`); setF((x) => ({ ...x, reason: "" })); load(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const del = async (p) => {
    if (!window.confirm(`Remove ${p.name}'s permission on ${p.date}?`)) return;
    try { await api.delete(`/biometric/short-permissions/${encodeURIComponent(p.email)}/${p.date}`); load(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const people = staff.filter((s) => s.email !== "ceo@nexsecure.in");
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4 sm:p-5" data-testid="permissions-panel">
      <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2 mb-1"><Clock3 size={13} className="text-nx-goldlight" /> Permissions (short leave)</p>
      <p className="text-[11px] text-slate-500 mb-3">Up to 1 h: no impact · over 1 h up to 2 h: Half Day · over 2 h: Absent (LOP). A mid-day out/in punch pair outside lunch is read as the permission; its gap is used if longer than granted. Probation half days count as 0.5 LOP. CEO exempt.</p>
      {canEdit && (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3">
          <select value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={`${sel} col-span-2`} style={dark} data-testid="perm-staff">
            <option value="" style={dark}>Select staff…</option>
            {people.map((s) => <option key={s.email} value={s.email} style={dark}>{s.name}</option>)}
          </select>
          <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={sel} style={dark} data-testid="perm-date" />
          <div className="flex gap-1">
            <input type="number" min="0" max="10" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} className={`${input} !px-2`} placeholder="h" title="Hours" data-testid="perm-hours" />
            <input type="number" min="0" max="59" step="5" value={f.minutes} onChange={(e) => setF({ ...f, minutes: e.target.value })} className={`${input} !px-2`} placeholder="m" title="Minutes" data-testid="perm-minutes" />
          </div>
          <input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} maxLength={120} placeholder="Reason" className={`${input} col-span-2 sm:col-span-1`} data-testid="perm-reason" />
          <button onClick={grant} disabled={busy || !f.email || mins <= 0} className="bg-nx-gold hover:bg-nx-goldlight disabled:opacity-50 text-nx-navy font-semibold text-xs tracking-[0.12em] uppercase px-3 py-2.5 col-span-2 sm:col-span-1" data-testid="perm-grant">
            {busy ? "Saving…" : `Grant${effect ? ` · ${effect}` : ""}`}
          </button>
        </div>
      )}
      {items.length === 0 ? <p className="text-xs text-slate-500">No permissions in {month}.</p> : (
        <div className="flex flex-col gap-1.5">
          {items.map((p) => (
            <div key={`${p.email}-${p.date}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs border border-white/10 px-3 py-2" data-testid={`perm-row-${p.email}-${p.date}`}>
              <span className="text-white font-medium">{p.name}</span><span className="text-slate-400">{p.date}</span>
              <span className="text-sky-300">{fmtMin(p.minutes)}</span>
              <span className={p.effect === "half day" ? "text-amber-300" : p.effect.startsWith("absent") ? "text-red-300" : "text-emerald-300"}>{p.effect}</span>
              {p.reason && <span className="text-slate-400">{p.reason}</span>}
              <span className="text-slate-500 ml-auto">by {p.by}</span>
              {canEdit && <button onClick={() => del(p)} className={`${btnGhost} !py-1 !px-2 text-[10px]`} data-testid={`perm-del-${p.email}-${p.date}`}><Trash2 size={11} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
